import * as schedulerService from '@/services/scheduler.service';

import type {
  BatchCallFormValues,
  BatchCallUpdateFormValues,
  RecipientUpdateFormValues,
} from '@/schemas/batch-call.schema';

import { queryKeys } from './query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from './query-notifications';

import type { PaginatedResponse, PaginationParams, QueryOptions } from '@/types/common.type';
import type {
  BatchCall,
  BatchCallDetail,
  BatchCallFilterParams,
  BatchRecipient,
} from '@/types/scheduler.type';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useBatchCallsQuery = (
  params: BatchCallFilterParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<BatchCall>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.batchCalls.list(
      params.page || 1,
      params.page_size,
      params.query,
      params.filters
    ),
    queryFn: () => schedulerService.fetchBatchCalls(params),
    ...options,
  });
};

export const useBatchCallDetailQuery = (
  id: number,
  options?: Omit<UseQueryOptions<BatchCallDetail, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.batchCalls.detail(id),
    queryFn: () => schedulerService.fetchBatchCallDetail(id),
    enabled: !!id,
    ...options,
  });
};

export const useBatchCallRecipientsQuery = (
  id: number,
  params: PaginationParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<BatchRecipient>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.batchCalls.recipients(id, params.page, params.page_size),
    queryFn: () => schedulerService.fetchBatchCallRecipients(id, params),
    enabled: !!id,
    ...options,
  });
};

export const useCreateBatchCallMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, BatchCallFormValues>({
    mutationKey: queryKeys.batchCalls.mutations.create,
    mutationFn: schedulerService.createBatchCall,
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Batch call created',
        'Your batch call was created successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.batchCalls.all });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Batch call creation failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDeleteBatchCallMutation = (options?: QueryOptions<null>) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationKey: queryKeys.batchCalls.mutations.delete,
    mutationFn: schedulerService.deleteBatchCall,
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey: queryKeys.batchCalls.all });
      const previousData = queryClient.getQueryData<PaginatedResponse<BatchCall>>(
        queryKeys.batchCalls.all
      );
      queryClient.setQueryData(
        queryKeys.batchCalls.all,
        (old: PaginatedResponse<BatchCall> | undefined) => {
          if (!old) return old;
          return { ...old, data: old.data.filter(c => c.id !== id) };
        }
      );
      return { previousData };
    },
    onError: (error, _id, context) => {
      const ctx = context as { previousData?: PaginatedResponse<BatchCall> } | undefined;
      if (ctx?.previousData) {
        queryClient.setQueryData(queryKeys.batchCalls.all, ctx.previousData);
      }
      notifyMutationErrorWithOptions(options, 'Delete failed', error);
      options?.onError?.(error);
    },
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Batch call deleted',
        'The batch call was deleted successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.batchCalls.all });
      options?.onSuccess?.(null);
    },
  });
};

export const useUpdateBatchCallMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: number; values: BatchCallUpdateFormValues }>({
    mutationKey: queryKeys.batchCalls.mutations.update,
    mutationFn: ({ id, values }) => schedulerService.updateBatchCall(id, values),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Batch call updated',
        'The batch call was updated successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.batchCalls.all });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Batch call update failed', error);
      options?.onError?.(error);
    },
  });
};

export const useUpdateBatchCallRecipientMutation = (
  batchCallId: number,
  options?: QueryOptions<void>
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { recipientId: number; values: RecipientUpdateFormValues }>({
    mutationKey: queryKeys.batchCalls.mutations.updateRecipient,
    mutationFn: ({ recipientId, values }) =>
      schedulerService.updateBatchCallRecipient(batchCallId, recipientId, values),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Recipient updated',
        'The recipient was updated successfully.'
      );
      queryClient.invalidateQueries({ queryKey: ['batch-call-recipients', batchCallId] });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Recipient update failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDownloadSampleTemplateMutation = (options?: QueryOptions<Blob>) => {
  return useMutation<Blob, Error, string>({
    mutationKey: queryKeys.batchCalls.mutations.downloadTemplate,
    mutationFn: schedulerService.downloadSampleTemplate,
    onSuccess: blob => {
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), {
        href: url,
        download: 'upload_template.csv',
      });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      options?.onSuccess?.(blob);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Download failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDeleteBatchCallRecipientMutation = (
  batchCallId: number,
  options?: QueryOptions<void>
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationKey: queryKeys.batchCalls.mutations.deleteRecipient,
    mutationFn: recipientId => schedulerService.deleteBatchCallRecipient(batchCallId, recipientId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Recipient deleted',
        'The recipient was deleted successfully.'
      );
      queryClient.invalidateQueries({ queryKey: ['batch-call-recipients', batchCallId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.batchCalls.detail(batchCallId) });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Recipient deletion failed', error);
      options?.onError?.(error);
    },
  });
};
